import React, { useContext, useEffect, useRef } from 'react';
import { FuelGridContainer, FuelGridItem } from '@frete.com/fuel-react';
import AppContext from '@context';
import { JourneyItemStateInterface } from '@context/reducers/journeys/journeys.type';
import useAmplitude from '@hooks/useAmplitude';
import { occurrenceRead } from '@services/journeys';
import { openCompanyEvaluating } from '@utils/companyEvaluating/companyEvaluating';
import JourneyListProposals from '@components/ui/JourneyListProposals';
import Map from '../../Map';
import Pagination from '../../Pagination';
import Error from '../Error';
import EmptyState from '../../EmptyState';
import When from '../../When';
import SkeletonAllTabDesktop from './Desktop/SkeletonAllTab';
import SkeletonAllTabMobile from './Mobile/SkeletonAllTabMobile';
import JourneysAllTabDesktop from './Desktop';
import JourneysAllTabMobile from './Mobile';
import Title from './Title';
import { JourneysAllTabContainerPropsInterface } from './JourneysAllTabContainerTypes';

function JourneysAllTabContainer({
  isMobile,
  handleClick,
  handlerModalDialogue,
  onCallbackSentWhatsApp,
  onClickUnrecognized,
  onClickCancelJourney,
  onErrorGetAgain,
  onChangePage,
  onClickActionMarker,
}: JourneysAllTabContainerPropsInterface) {
  const { amplitudeEvent } = useAmplitude();
  const {
    state: {
      journeys: { all: journeys },
    },
    dispatch,
  } = useContext(AppContext);
  const journeysContentRef = useRef<HTMLDivElement>(null);

  function renderMap() {
    return (
      <FuelGridItem sm={2} md={2} lg={4} xlg={4}>
        <Map
          journeys={journeys.data}
          onClickActionMarker={onClickActionMarker}
        />
      </FuelGridItem>
    );
  }

  function renderPagination() {
    return (
      <Pagination
        onChange={onChangePage}
        total={journeys.meta.total}
        page={journeys.meta.page}
        isMobile={isMobile}
      />
    );
  }

  function handleEvaluatingJourneyList(id: JourneyItemStateInterface['id']) {
    openCompanyEvaluating(id);
    amplitudeEvent('company_evaluating_button_journey_list', {
      travelId: id || '',
    });
  }

  function renderJourneys() {
    if (journeys.isFetching && !isMobile) {
      return <SkeletonAllTabDesktop />;
    }

    if (journeys.isFetching && isMobile) {
      return <SkeletonAllTabMobile />;
    }

    if (journeys.error) {
      return <Error onClick={() => onErrorGetAgain()} />;
    }

    if (!journeys.data || !journeys.data.length) {
      return <EmptyState />;
    }

    if (journeys.data && journeys.data.length) {
      return (
        <>
          <When isShow={!isMobile}>
            <FuelGridContainer gapRow={24} style={{ margin: 0 }}>
              <FuelGridItem sm={4} md={6} lg={8} xlg={8}>
                <JourneyListProposals isMobile={isMobile} />
                <Title />
                <div ref={journeysContentRef}>
                  <div data-testid="JourneysAllListDesktop">
                    {journeys.data.map((item, index, arrayCurrent) => (
                      <JourneysAllTabDesktop
                        key={item.id}
                        {...item}
                        isMobile={isMobile}
                        handleClick={(event) => handleClick(event, item)}
                        handlerModalDialogue={handlerModalDialogue}
                        onCallbackSentWhatsApp={onCallbackSentWhatsApp}
                        onClickUnrecognized={onClickUnrecognized}
                        onClickCancelJourney={onClickCancelJourney}
                        onClickEvaluatingJourneyList={(id) =>
                          handleEvaluatingJourneyList(id)
                        }
                        hasBorder={index + 1 !== arrayCurrent.length}
                      />
                    ))}
                  </div>
                  {!journeys.isFetching &&
                    journeys.data &&
                    journeys.data.length &&
                    renderPagination()}
                </div>
              </FuelGridItem>
              {!journeys.isFetching &&
                journeys.data &&
                journeys.data.length &&
                renderMap()}
            </FuelGridContainer>
          </When>
          <When isShow={isMobile}>
            <JourneyListProposals isMobile={isMobile} />
            <div data-testid="JourneysAllListMobile">
              {journeys.data.map((item, index, arrayCurrent) => (
                <JourneysAllTabMobile
                  key={item.id}
                  {...item}
                  isMobile={isMobile}
                  handleClick={(event) => handleClick(event, item)}
                  handlerModalDialogue={handlerModalDialogue}
                  onCallbackSentWhatsApp={onCallbackSentWhatsApp}
                  onClickUnrecognized={onClickUnrecognized}
                  onClickCancelJourney={onClickCancelJourney}
                  onClickEvaluatingJourneyList={(id) =>
                    handleEvaluatingJourneyList(id)
                  }
                  hasBorder={index + 1 !== arrayCurrent.length}
                />
              ))}
            </div>
            {!journeys.isFetching &&
              journeys.data &&
              journeys.data.length &&
              renderPagination()}
          </When>
        </>
      );
    }

    return null;
  }

  function handleOccurrenceView(
    dataEvent: CustomEvent<{
      journeyId: string;
      occurrenceId: string;
      alreadyRead: boolean;
    }>,
  ) {
    const { occurrenceId, journeyId, alreadyRead } = dataEvent.detail;

    if (!alreadyRead) {
      occurrenceRead(occurrenceId).then(() => {
        dispatch({
          type: 'journey_occurrence_read',
          payload: {
            journeyId,
            occurrenceId,
            read: true,
          },
        });
      });
    }
  }

  function handleRemoveOccurenceMap(
    dataEvent: CustomEvent<{ journeyId: string }>,
  ) {
    dispatch({
      type: 'journey_occurrence_remove',
      payload: {
        journeyId: dataEvent.detail.journeyId,
      },
    });
  }

  useEffect(() => {
    window.addEventListener('mapOccurrenceViewed', handleOccurrenceView);
    window.addEventListener('mapOccurrenceRemove', handleRemoveOccurenceMap);

    return () => {
      window.removeEventListener('mapOccurrenceViewed', handleOccurrenceView);
      window.removeEventListener(
        'mapOccurrenceRemove',
        handleRemoveOccurenceMap,
      );
    };
  }, []);

  return <>{renderJourneys()}</>;
}

export default JourneysAllTabContainer;
